import {
  useOrganisationList,
  useSiteList,
} from "@inrange/building-manager-api-client";
import { Organisation } from "@inrange/building-manager-api-client/models-organisation";
import { Site } from "@inrange/building-manager-api-client/models-site";
import { sortBy } from "@inrange/utils";
import { ReactNode, useState } from "react";
import { Container } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import Loading from "../Loading";
import SitesTable from "../SitesTable";
import { IN_PROGRESS_STATUSES } from "./operationalStatusUtils";
import SiteListGeo from "./siteSections/SiteGeo/SiteListGeo";
import {
  isLinkedToNonTestOrgWithNameCheck,
  isProdSiteBasedOnName,
} from "./siteSections/utils";

const FILTER_KEYS = ["production", "operational", "operationalOfftaker", "inprogress", "started", "locked"];

const parseFiltersFromParams = (
  searchParams: URLSearchParams
): Record<string, boolean> => {
  const filters: Record<string, boolean> = {};
  for (const key of FILTER_KEYS) {
    if (searchParams.get(key) === "true") {
      filters[key] = true;
    }
  }
  return filters;
};

const SiteList = () => {
  document.title = "Site List";

  const [searchParams, setSearchParams] = useSearchParams();
  const testOrgId = searchParams.get("testOrgId");

  const [siteIdsInRegion, setSiteIdsInRegion] = useState<
    string[] | undefined
  >(undefined);
  const [_sitesInViewLoading, setSitesInViewLoading] = useState(true);
  const [filteredLinkedOrgIds, setFilteredLinkedOrgIds] = useState<string[]>(
    testOrgId ? [testOrgId] : []
  );
  const [filters, setFilters] = useState<Record<string, boolean>>(
    parseFiltersFromParams(searchParams)
  );

  const onSetFilters = (newFilters: Record<string, boolean>) => {
    setFilters(newFilters);
    const newParams = new URLSearchParams(searchParams);
    for (const key of FILTER_KEYS) {
      if (newFilters[key]) {
        newParams.set(key, "true");
      } else {
        newParams.delete(key);
      }
    }
    setSearchParams(newParams, { replace: true });
  };

  const [tableAlertStyle, setTableAlertStyle] = useState("secondary");
  const [tableAlertMessage, setTableAlertMessage] = useState<
    ReactNode | string | undefined
  >(undefined);

  const { fetchSites } = useSiteList();
  const { fetchOrganisations } = useOrganisationList();

  if (fetchSites.isLoading || fetchOrganisations.isLoading)
    return <Loading label="Loading sites data..." />;

  if (fetchSites.isError || fetchOrganisations.isError) {
    return <div>Error loading the sites.</div>;
  }

  const sites = (fetchSites.data?.sites ?? []) as Site[];

  const organisations: Organisation[] = (
    fetchOrganisations.data?.organisations ?? []
  ).sort((a, b) => {
    const aName = a.name.toUpperCase();
    const bName = b.name.toUpperCase();
    return aName < bName ? -1 : aName > bName ? 1 : 0;
  });

  const filterOrgs = [...organisations]
    .map((org) => {
      return { name: org.name, orgId: org.id };
    })
    .sort(sortBy((so) => so.name));

  // First filter by operational status for the map
  const sitesForMap = sites.filter((site) => {
    const hasStatusFilter =
      filters.operational ||
      filters.operationalOfftaker ||
      filters.inprogress ||
      filters.started;
    if (hasStatusFilter) {
      const isPv = site.totalInstalledCapacity > 0;
      const matchesOperationalPv =
        filters.operational &&
        site.operationalStatus === "operational" &&
        isPv;
      const matchesOfftaker =
        filters.operationalOfftaker &&
        site.operationalStatus === "operational" &&
        !isPv;
      const matchesInProgress =
        filters.inprogress &&
        IN_PROGRESS_STATUSES.has(site.operationalStatus);
      const matchesStarted =
        filters.started && site.operationalStatus !== "notStarted";
      if (
        !matchesOperationalPv &&
        !matchesOfftaker &&
        !matchesInProgress &&
        !matchesStarted
      ) {
        return false;
      }
    }
    if (
      filters.production &&
      (!isLinkedToNonTestOrgWithNameCheck(site) || !isProdSiteBasedOnName(site))
    ) {
      return false;
    }
    if (filters.locked && !site.locked) {
      return false;
    }
    return true;
  });

  // Then apply region and org filters
  const filteredSites = sitesForMap
    .filter((site) => !siteIdsInRegion || siteIdsInRegion.includes(site.id))
    .filter(
      (site) =>
        filteredLinkedOrgIds.length === 0 ||
        site.siteOwnerships.some(
          (siteOwnership) =>
            filteredLinkedOrgIds.indexOf(siteOwnership.orgID) > -1
        )
    );

  return (
    <>
      <Container fluid style={{ padding: "12px 12px 0px 12px", minHeight: "90vh" }}>
        <SiteListGeo
          sites={sitesForMap}
          selectedOrgs={filteredLinkedOrgIds}
          setSiteIdsInRegion={setSiteIdsInRegion}
          setSitesInViewLoading={setSitesInViewLoading}
        />
        <SitesTable
          isFullSiteList={true}
          sites={filteredSites}
          orgFilterLabel={"Organisation"}
          filterOrgs={filterOrgs}
          setFilteredLinkedOrgIds={setFilteredLinkedOrgIds}
          tableAlertStyle={tableAlertStyle}
          setTableAlertStyle={setTableAlertStyle}
          tableAlertMessage={tableAlertMessage}
          setTableAlertMessage={setTableAlertMessage}
          filters={filters}
          setFilters={onSetFilters}
          defaultSortOrder={searchParams.get("sort") ?? undefined}
          defaultSortDirection={searchParams.get("sortDir") ?? undefined}
        />
      </Container>
    </>
  );
};

export default SiteList;
